import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Redirect } from 'react-router-dom';
import { Form, Input, Button } from 'antd';
import { isMobileOnly } from 'react-device-detect';
import styled, { css } from 'styled-components';

import { useForm } from 'src/utils/hooks';
import { LOGIN_USER } from 'src/operations/queries/getUser';
import { isLogedIn } from 'src/apollo/cache';
import Loading from 'src/components/common/Loading';
import { storage } from 'src/utils/hooks';
import { getCurrentToken } from 'src/utils';
import queryString from 'querystring-es3';

const LoginContainer = styled.div`
  font-family: 'Poppins', 'Noto Sans KR', sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: auto;
  padding: 40px 0;

  ${(props) =>
    props.$forceMobile &&
    css`
      height: 100vh;
      height: 100dvh;
      min-height: 0;
      overflow: hidden;
      padding: 5px;
    `}

  @media (max-width: 968px) {
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
    padding: 5px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    padding: 3px;
  }
`;

const GradientBg = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background:
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 20%, rgba(255, 255, 255, 0.12) 0%, transparent 50%);
    animation: gradientShift 15s ease infinite;
  }

  @keyframes gradientShift {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-50px, -50px);
    }
  }
`;

const FloatingShapes = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const Shape = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: float 20s infinite ease-in-out;

  &:nth-child(1) {
    width: 400px;
    height: 400px;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    top: -100px;
    left: -100px;
    animation-delay: 0s;
  }

  &:nth-child(2) {
    width: 350px;
    height: 350px;
    background: linear-gradient(135deg, #34d399, #10b981);
    bottom: -50px;
    right: -50px;
    animation-delay: 5s;
  }

  &:nth-child(3) {
    width: 300px;
    height: 300px;
    background: linear-gradient(135deg, #60a5fa, #3b82f6);
    top: 50%;
    left: 50%;
    animation-delay: 10s;
  }

  @keyframes float {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(100px, -100px) scale(1.1);
    }
    66% {
      transform: translate(-100px, 100px) scale(0.9);
    }
  }
`;

const LoginWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1100px;
  padding: 20px;
  display: flex;
  gap: 60px;
  align-items: flex-start;
  justify-content: center;

  ${(props) =>
    props.$forceMobile &&
    css`
      flex-direction: column;
      max-width: 480px;
      gap: 8px;
      padding: 10px;
      max-height: 100%;
      align-items: stretch;
    `}

  @media (max-width: 968px) {
    flex-direction: column;
    max-width: 480px;
    gap: 8px;
    padding: 10px;
    max-height: 100%;
    align-items: stretch;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    gap: 6px;
    padding: 6px;
  }
`;

const BrandingSide = styled.div`
  flex: 1;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${(props) =>
    props.$forceMobile &&
    css`
      gap: 8px;
      text-align: left;
      align-items: stretch;
    `}

  @media (max-width: 968px) {
    gap: 8px;
    text-align: left;
    align-items: stretch;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    gap: 6px;
  }
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  ${(props) =>
    props.$forceMobile &&
    css`
      justify-content: flex-start;
      gap: 10px;
      padding-left: 16px;
    `}

  @media (max-width: 968px) {
    justify-content: flex-start;
    gap: 10px;
    padding-left: 16px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    gap: 8px;
  }
`;

const EvineLogo = styled.img`
  width: 330px;
  max-width: 100%;
  object-fit: contain;

  ${(props) =>
    props.$forceMobile &&
    css`
      width: 250px;
    `}

  @media (max-width: 968px) {
    width: 250px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    width: 220px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 20px;
  color: white;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);

  ${(props) =>
    props.$forceMobile &&
    css`
      font-size: 20px;
      margin: 0 0 4px;
      line-height: 1.1;
      padding-left: 16px;
    `}

  @media (max-width: 968px) {
    font-size: 20px;
    margin: 0 0 4px;
    line-height: 1.1;
    padding-left: 16px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    font-size: 18px;
    margin-bottom: 3px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 19px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin-bottom: 40px;

  ${(props) =>
    props.$forceMobile &&
    css`
      font-size: 11px;
      margin: 0 0 8px;
      line-height: 1.3;
      padding-left: 16px;
    `}

  @media (max-width: 968px) {
    font-size: 11px;
    margin: 0 0 8px;
    line-height: 1.3;
    padding-left: 16px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    font-size: 10px;
    margin-bottom: 6px;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${(props) =>
    props.$forceMobile &&
    css`
      gap: 6px;
      padding: 0 16px;
    `}

  @media (max-width: 968px) {
    gap: 6px;
    padding: 0 16px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    gap: 5px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 17px;
  color: white;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 16px 20px;
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateX(10px);
  }

  ${(props) =>
    props.$forceMobile &&
    css`
      padding: 8px 12px;
      font-size: 11px;
      border-radius: 10px;
      gap: 10px;
      justify-content: flex-start;
    `}

  @media (max-width: 968px) {
    padding: 8px 12px;
    font-size: 11px;
    border-radius: 10px;
    gap: 10px;
    justify-content: flex-start;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    padding: 6px 10px;
    font-size: 10px;
  }
`;

const FeatureIcon = styled.div`
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  color: red;

  ${(props) =>
    props.$forceMobile &&
    css`
      width: 22px;
      height: 22px;
      font-size: 12px;
      border-radius: 6px;
    `}

  @media (max-width: 968px) {
    width: 22px;
    height: 22px;
    font-size: 12px;
    border-radius: 6px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    width: 20px;
    height: 20px;
    font-size: 11px;
  }
`;

const LoginSide = styled.div`
  flex: 0 0 440px;

  @media (max-width: 968px) {
    flex: 1;
    width: 100%;
  }
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 32px;
  padding: 48px 48px 84px;
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.5) inset;
  animation: cardSlide 0.6s ease-out;

  @keyframes cardSlide {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${(props) =>
    props.$forceMobile &&
    css`
      border-radius: 20px;
      padding: 20px 18px;
    `}

  @media (max-width: 968px) {
    border-radius: 20px;
    padding: 20px 18px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    padding: 16px 14px;
  }
`;

const CardHeader = styled.div`
  margin-bottom: 36px;

  ${(props) =>
    props.$forceMobile &&
    css`
      margin-bottom: 16px;
    `}

  @media (max-width: 968px) {
    margin-bottom: 16px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    margin-bottom: 12px;
  }
`;

const CardTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #00d4ff, #0099ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;

  ${(props) =>
    props.$forceMobile &&
    css`
      font-size: 20px;
      margin-bottom: 4px;
    `}

  @media (max-width: 968px) {
    font-size: 20px;
    margin-bottom: 4px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    font-size: 18px;
  }
`;

const CardSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;

  ${(props) =>
    props.$forceMobile &&
    css`
      font-size: 11px;
      margin: 0;
    `}

  @media (max-width: 968px) {
    font-size: 11px;
    margin: 0;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    font-size: 10px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;

  ${(props) =>
    props.$forceMobile &&
    css`
      margin-bottom: 12px;
    `}

  @media (max-width: 968px) {
    margin-bottom: 12px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    margin-bottom: 10px;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;

  ${(props) =>
    props.$forceMobile &&
    css`
      font-size: 11px;
      margin-bottom: 6px;
    `}

  @media (max-width: 968px) {
    font-size: 11px;
    margin-bottom: 6px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    font-size: 10px;
    margin-bottom: 5px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  z-index: 1;

  ${(props) =>
    props.$forceMobile &&
    css`
      left: 14px;
      font-size: 16px;
    `}

  @media (max-width: 968px) {
    left: 14px;
    font-size: 16px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    left: 12px;
    font-size: 14px;
  }
`;

const LoginInput = styled(Input)`
  width: 100%;
  padding: 16px 20px 16px 56px;
  font-size: 15px;
  color: #1e293b;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;

  &:focus {
    outline: none;
    background: white;
    border-color: #00d4ff;
    box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }

  ${(props) =>
    props.$forceMobile &&
    css`
      padding: 11px 14px 11px 42px;
      font-size: 13px;
      border-radius: 10px;
    `}

  @media (max-width: 968px) {
    padding: 11px 14px 11px 42px;
    font-size: 13px;
    border-radius: 10px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    padding: 10px 12px 10px 38px;
    font-size: 12px;
  }
`;

const PasswordInput = styled(Input.Password)`
  width: 100%;
  padding: 16px 20px 16px 56px;
  font-size: 15px;
  color: #1e293b;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;

  input,
  .ant-input {
    background: #f8fafc;
    color: #1e293b;
  }

  input::placeholder,
  .ant-input::placeholder {
    background: #f8fafc;
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    background: white;
    border-color: #00d4ff;
    box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.1);
  }

  ${(props) =>
    props.$forceMobile &&
    css`
      padding: 11px 14px 11px 42px;
      font-size: 13px;
      border-radius: 10px;
    `}

  @media (max-width: 968px) {
    padding: 11px 14px 11px 42px;
    font-size: 13px;
    border-radius: 10px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    padding: 10px 12px 10px 38px;
    font-size: 12px;
  }
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;

  ${(props) =>
    props.$forceMobile &&
    css`
      margin-bottom: 14px;
    `}

  @media (max-width: 968px) {
    margin-bottom: 14px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    margin-bottom: 12px;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  input[type='checkbox'] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #00d4ff;
  }

  ${(props) =>
    props.$forceMobile &&
    css`
      input[type='checkbox'] {
        width: 16px;
        height: 16px;
      }
    `}

  @media (max-width: 968px) {
    input[type='checkbox'] {
      width: 16px;
      height: 16px;
    }
  }
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #475569;
  cursor: pointer;
  user-select: none;
  font-weight: 500;

  ${(props) =>
    props.$forceMobile &&
    css`
      font-size: 11px;
    `}

  @media (max-width: 968px) {
    font-size: 11px;
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  padding: 18px;
  font-size: 17px;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #00d4ff, #0099ff);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.3);
  position: relative;
  overflow: hidden;
  height: unset;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0, 212, 255, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }

  ${(props) =>
    props.$forceMobile &&
    css`
      padding: 13px;
      font-size: 14px;
      border-radius: 10px;
    `}

  @media (max-width: 968px) {
    padding: 13px;
    font-size: 14px;
    border-radius: 10px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    padding: 11px;
    font-size: 13px;
  }
`;

const LoginFooter = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  text-align: center;

  ${(props) =>
    props.$forceMobile &&
    css`
      margin-top: 16px;
      padding-top: 12px;
    `}

  @media (max-width: 968px) {
    margin-top: 16px;
    padding-top: 12px;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    margin-top: 12px;
    padding-top: 10px;
  }
`;

const LoginFooterText = styled.p`
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.6;

  ${(props) =>
    props.$forceMobile &&
    css`
      font-size: 9px;
      line-height: 1.4;
      margin: 0;
    `}

  @media (max-width: 968px) {
    font-size: 9px;
    line-height: 1.4;
    margin: 0;
  }

  @media (max-width: 640px) and (max-height: 700px) {
    font-size: 8px;
  }
`;

const ErrorMessage = styled.h4`
  color: red;
  min-height: 20px;
  margin: 0 0 16px;
  text-align: center;

  ${(props) =>
    props.$forceMobile &&
    css`
      min-height: 14px;
      margin-bottom: 8px;
      font-size: 12px;
    `}

  @media (max-width: 968px) {
    min-height: 14px;
    margin-bottom: 8px;
    font-size: 12px;
  }
`;

const Login = () => {
  const [err, setErr] = useState('');
  const token = getCurrentToken();
  const forceMobileLayout = isMobileOnly || window.isNativeAppMobile;
  const parsed = queryString.parse(window.location.search.replace('?', ''));
  const cidx = parseInt(parsed.campus_idx);

  const { onChange, onSubmit, values } = useForm(loginUserCallback, {
    id: storage.getItem('culp_userid') || '',
    pw: storage.getItem('culp_pw') || '',
    campus_idx: cidx || 3,
    remember: storage.getItem('culp_userid') ? true : false,
  });

  const [loginUser, { loading, data, error }] = useLazyQuery(LOGIN_USER);

  const handleCredentialChange = (event) => {
    onChange({
      target: {
        name: event.target.name,
        value: event.target.value || '',
      },
    });
  };

  const handleRememberChange = (event) => {
    onChange({
      target: {
        name: 'remember',
        checked: event.target.checked,
        value: '',
      },
    });
  };

  function loginUserCallback() {
    loginUser({
      variables: values,
    });
  }

  useEffect(() => {
    if (data?.login) {
      storage.setItem('culp_token', JSON.stringify(data.login));
      if (window.Android && window.Android.SaveUserToken) {
        window.Android.SaveUserToken(JSON.stringify(data.login));
      }
      isLogedIn(!!data?.login);
      window.location.reload();
    }

    if (values.remember) {
      storage.setItem('culp_userid', values.id);
      storage.setItem('culp_pw', values.pw);
    } else {
      storage.removeItem('culp_userid');
      storage.removeItem('culp_pw');
    }
  }, [data, values]);

  useEffect(() => {
    if (error) {
      setErr(error.message);
      setTimeout(() => {
        setErr('');
      }, 2000);
    }
  }, [error]);

  if (token) return <Redirect to="/" />;

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <LoginContainer $forceMobile={forceMobileLayout}>
          <GradientBg />
          <FloatingShapes>
            <Shape />
            <Shape />
            <Shape />
          </FloatingShapes>

          <LoginWrapper $forceMobile={forceMobileLayout}>
            <BrandingSide $forceMobile={forceMobileLayout}>
              <BrandLogo $forceMobile={forceMobileLayout}>
                <EvineLogo $forceMobile={forceMobileLayout} src="/images/evinelogo.png" alt="English Vine Logo" />
              </BrandLogo>

              <div>
                <HeroTitle $forceMobile={forceMobileLayout}>
                  우리 학원의
                  <br />
                  스마트한 성장
                </HeroTitle>
                <HeroSubtitle $forceMobile={forceMobileLayout}>선생님과 학생을 하나로 연결하는 통합 학습 관리 시스템</HeroSubtitle>
              </div>

              <FeatureList $forceMobile={forceMobileLayout}>
                <FeatureItem $forceMobile={forceMobileLayout}>
                  <FeatureIcon $forceMobile={forceMobileLayout}>✓</FeatureIcon>
                  <span>학생별 맞춤 학습 지도 추적</span>
                </FeatureItem>
                <FeatureItem $forceMobile={forceMobileLayout}>
                  <FeatureIcon $forceMobile={forceMobileLayout}>✓</FeatureIcon>
                  <span>AI 기반 성적 분석 리포트</span>
                </FeatureItem>
                <FeatureItem $forceMobile={forceMobileLayout}>
                  <FeatureIcon $forceMobile={forceMobileLayout}>✓</FeatureIcon>
                  <span>언제 어디서나 쉬운 접속</span>
                </FeatureItem>
              </FeatureList>
            </BrandingSide>

            <LoginSide>
              <LoginCard $forceMobile={forceMobileLayout}>
                <CardHeader $forceMobile={forceMobileLayout}>
                  <CardTitle $forceMobile={forceMobileLayout}>환영합니다!</CardTitle>
                  <CardSubtitle $forceMobile={forceMobileLayout}>계정에 로그인하여 시작하세요</CardSubtitle>
                </CardHeader>

                <ErrorMessage $forceMobile={forceMobileLayout}>{err && err}</ErrorMessage>

                <Form
                  id="loginForm"
                  onFinish={onSubmit}
                  initialValues={{
                    userid: values.id,
                    password: values.pw,
                  }}
                >
                  <FormGroup $forceMobile={forceMobileLayout}>
                    <FormLabel $forceMobile={forceMobileLayout} htmlFor="username">
                      아이디
                    </FormLabel>
                    <InputWrapper>
                      <InputIcon $forceMobile={forceMobileLayout}>👤</InputIcon>
                      <LoginInput
                        $forceMobile={forceMobileLayout}
                        type="text"
                        id="username"
                        name="id"
                        placeholder="아이디를 입력하세요"
                        onChange={handleCredentialChange}
                        value={values.id}
                        required
                      />
                    </InputWrapper>
                  </FormGroup>

                  <FormGroup $forceMobile={forceMobileLayout}>
                    <FormLabel $forceMobile={forceMobileLayout} htmlFor="password">
                      비밀번호
                    </FormLabel>
                    <InputWrapper>
                      <InputIcon $forceMobile={forceMobileLayout}>🔒</InputIcon>
                      <PasswordInput
                        $forceMobile={forceMobileLayout}
                        type="password"
                        id="password"
                        name="pw"
                        placeholder="비밀번호를 입력하세요"
                        onChange={handleCredentialChange}
                        value={values.pw}
                        required
                      />
                    </InputWrapper>
                  </FormGroup>

                  <FormFooter $forceMobile={forceMobileLayout}>
                    <CheckboxWrapper $forceMobile={forceMobileLayout}>
                      <input
                        type="checkbox"
                        id="remember"
                        name="remember"
                        onChange={handleRememberChange}
                        checked={Boolean(values.remember)}
                      />
                      <CheckboxLabel $forceMobile={forceMobileLayout} htmlFor="remember">
                        Remember me
                      </CheckboxLabel>
                    </CheckboxWrapper>
                  </FormFooter>

                  <LoginButton $forceMobile={forceMobileLayout} type="primary" htmlType="submit" loading={loading}>
                    로그인
                  </LoginButton>
                </Form>

                <LoginFooter $forceMobile={forceMobileLayout}>
                  <LoginFooterText $forceMobile={forceMobileLayout}>
                    개인정보 취급방침
                    <br />
                    <strong>Cloubot. All rights reserved.</strong>
                  </LoginFooterText>
                </LoginFooter>
              </LoginCard>
            </LoginSide>
          </LoginWrapper>
        </LoginContainer>
      )}
    </>
  );
};

export default Login;
